import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, MessageCircle, RotateCcw } from "lucide-react";
import { useLang } from "../../lib/LangContext";
import { p22Copy } from "../../lib/p22Copy";
import { useBookingWizard } from "./useBookingWizard";
import { Progress } from "./WizardBits";
import { StepService } from "./StepService";
import { StepLocation } from "./StepLocation";
import { StepAppointment } from "./StepAppointment";
import { StepDetails } from "./StepDetails";
import { StepReview } from "./StepReview";

const STEPS = [StepService, StepLocation, StepAppointment, StepDetails, StepReview];

const Done = ({ c, w }) => {
  const d = c.done, wa = w.result.via === "wa";
  return (
    <div data-testid="wizard-done" className="py-4">
      <CheckCircle2 size={30} className="text-gold" />
      <h3 className="mt-6 font-heading text-white text-2xl md:text-3xl tracking-tight">{wa ? d.titleWa : d.titleForm}</h3>
      <p className="mt-4 text-[15px] text-zinc-300 leading-relaxed max-w-xl">{wa ? d.textWa : d.textForm}</p>
      <p className="mt-3 text-[13px] text-zinc-400">{c.response}</p>
      <p className="mt-6 text-[12px] tracking-[0.25em] uppercase text-zinc-500">
        {d.reference} <span data-testid="wizard-done-ref" className="ml-2 font-mono text-gold tracking-[0.15em]">{w.result.id.slice(0, 8).toUpperCase()}</span>
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <a href={w.result.waUrl} target="_blank" rel="noreferrer" data-testid="wizard-done-reopen"
          className="inline-flex items-center justify-center gap-3 bg-gold text-black px-6 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-[#E5C378] transition-colors">
          <MessageCircle size={16} /> {wa ? d.reopen : c.review.primary}
        </a>
        <button type="button" data-testid="wizard-done-new" onClick={w.reset}
          className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-6 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-gold hover:text-gold transition-colors">
          <RotateCcw size={14} /> {d.newRequest}
        </button>
      </div>
    </div>
  );
};

export const BookingWizard = () => {
  const { lang, t } = useLang();
  const c = p22Copy(lang).booking;
  const w = useBookingWizard(c, lang);
  const reduce = useReducedMotion();
  const panel = useRef(null), heading = useRef(null);
  const [touched, setTouched] = useState(false);
  const Step = STEPS[w.step];

  useEffect(() => {
    if (!touched) return;
    heading.current?.focus({ preventScroll: true });
    const top = panel.current?.getBoundingClientRect().top ?? 0;
    if (top < 0) panel.current.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, [w.step, touched, reduce]);

  const markActive = () => { document.body.dataset.bookingActive = "1"; };
  const go = (fn) => () => { setTouched(true); fn(); };
  const anim = reduce ? {} : { initial: { opacity: 0, x: 24 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -24 }, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } };

  return (
    <div ref={panel} onPointerDown={markActive} onFocus={markActive} data-testid="booking-wizard" className="scroll-mt-24 bg-[#0A0A0C] border border-white/[0.08] p-5 sm:p-7 md:p-10">
      {w.result ? <Done c={c} w={w} /> : (
        <>
          <Progress steps={c.steps} step={w.step} onJump={(i) => { setTouched(true); w.goTo(i); }} />
          <p className="sm:hidden mt-3 text-[11px] tracking-[0.25em] uppercase text-gold">{String(w.step + 1).padStart(2, "0")} / 05 · {c.steps[w.step]}</p>
          <h3 ref={heading} tabIndex={-1} data-testid="wizard-step-title" className="mt-8 md:mt-10 mb-6 md:mb-8 font-heading text-white text-2xl md:text-3xl tracking-tight focus:outline-none">
            {c.stepTitles[w.step]}
          </h3>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={w.step} {...anim} data-testid={`wizard-step-${w.step}`}>
              <Step c={c} w={w} lang={lang} privacyLabel={t.footer.datenschutz} />
            </motion.div>
          </AnimatePresence>
          <div className="mt-8 pt-6 border-t border-white/[0.08] flex items-center justify-between gap-4">
            <button type="button" data-testid="wizard-back" onClick={go(w.back)} disabled={w.step === 0}
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-zinc-300 hover:text-gold transition-colors disabled:opacity-0 disabled:pointer-events-none">
              <ArrowLeft size={14} /> {c.nav.back}
            </button>
            {w.step < 4 && (
              <button type="button" data-testid="wizard-next" onClick={go(w.next)}
                className="group inline-flex items-center gap-3 bg-gold text-black px-7 py-3.5 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-[#E5C378] transition-colors">
                {c.nav.next} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
